import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Footer from "@/components/Footer";

/**
 * Iter XX.45 — Legal page renderer.
 * Route: `/legal/:policyType` → GET /api/policies/{policyType}.
 *
 * Renders the policy summary (short bullets) + expandable full body.
 * Mobile-first. Zero heavy dependencies — the "markdown" is intentionally
 * simple; we only need headings, paragraphs and bold, so we render a
 * small subset manually.
 */
export default function LegalPage() {
  const { policyType } = useParams();
  const [policy, setPolicy] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/policies/${encodeURIComponent(policyType)}`);
        if (!alive) return;
        setPolicy(r.data);
      } catch {
        if (alive) setErr("not_found");
      }
    })();
    return () => { alive = false; };
  }, [policyType]);

  if (err) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-2xl font-semibold mb-2">Policy not found</div>
          <Link to="/trust" className="text-emerald-400 hover:text-emerald-300">← Back to Trust Center</Link>
        </div>
      </div>
    );
  }
  if (!policy) {
    return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col" data-testid={`legal-page-${policyType}`}>
      <div className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <Link to="/trust" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="legal-back">← Trust Center</Link>
        <h1 className="text-3xl sm:text-4xl font-semibold leading-tight mt-3 mb-1" data-testid="legal-title">{policy.title}</h1>
        <div className="text-xs text-neutral-500 mb-6">
          Version <span className="text-neutral-300" data-testid="legal-version">{policy.version}</span> · Effective {new Date(policy.effective_at).toLocaleDateString()}
        </div>

        {/* Summary bullets */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-6" data-testid="legal-summary">
          <div className="text-sm text-neutral-300 mb-3">Plain-English summary</div>
          <ul className="space-y-2">
            {(policy.summary || []).map((s, i) => (
              <li key={i} className="text-sm text-neutral-200 flex gap-2" data-testid={`legal-summary-item-${i}`}>
                <span className="text-emerald-400 shrink-0">•</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={() => setExpanded(v => !v)}
          className="text-sm text-emerald-400 hover:text-emerald-300 mb-4"
          data-testid="legal-toggle-full"
        >
          {expanded ? "Hide full text ↑" : "Read the full document ↓"}
        </button>

        {expanded && (
          <div className="prose prose-invert max-w-none text-neutral-200" data-testid="legal-full">
            {renderSimpleMarkdown(policy.full || "")}
          </div>
        )}

        <div className="mt-10 text-xs text-neutral-500">
          Previous versions are preserved and available on request.
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Minimal markdown-ish renderer. Supports `# ` / `## ` / `### `
// headings, `- ` bullets, `**bold**`, and paragraphs. No third-party
// dep needed; we own the source text.
function renderSimpleMarkdown(src) {
  const lines = String(src || "").split(/\r?\n/);
  const out = [];
  let bulletBuf = [];
  const flushBullets = () => {
    if (!bulletBuf.length) return;
    out.push(<ul key={`ul-${out.length}`} className="list-disc list-outside pl-6 space-y-1 my-3">
      {bulletBuf.map((b, i) => <li key={i}>{inlineFmt(b)}</li>)}
    </ul>);
    bulletBuf = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (line.startsWith("### ")) { flushBullets(); out.push(<h3 key={i} className="text-base font-semibold mt-6 mb-2">{line.slice(4)}</h3>); continue; }
    if (line.startsWith("## ")) { flushBullets(); out.push(<h2 key={i} className="text-lg font-semibold mt-8 mb-2 text-emerald-200">{line.slice(3)}</h2>); continue; }
    if (line.startsWith("# ")) { flushBullets(); out.push(<h1 key={i} className="text-2xl font-semibold mt-2 mb-4">{line.slice(2)}</h1>); continue; }
    if (line.startsWith("- ")) { bulletBuf.push(line.slice(2)); continue; }
    if (line === "") { flushBullets(); continue; }
    flushBullets();
    out.push(<p key={i} className="text-sm leading-relaxed my-2">{inlineFmt(line)}</p>);
  }
  flushBullets();
  return out;
}

function inlineFmt(s) {
  // Bold via **…**, link via [text](url).
  const parts = [];
  let cursor = 0;
  const re = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let m;
  let k = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > cursor) parts.push(s.slice(cursor, m.index));
    if (m[1]) parts.push(<strong key={k++}>{m[2]}</strong>);
    else if (m[3]) parts.push(<a key={k++} href={m[5]} className="text-emerald-400 hover:text-emerald-300 underline decoration-dotted">{m[4]}</a>);
    cursor = m.index + m[0].length;
  }
  if (cursor < s.length) parts.push(s.slice(cursor));
  return parts;
}
